import { useCallback, useEffect, useRef, useState } from 'react';
import { useFlushOnPageHide } from '@/hooks/use-flush-on-page-hide';
import { useMutation } from '@apollo/client';
import { UPDATE_CLIP_DATA } from '@/queries/queries';
import {
  addLayer as addLayerOp,
  updateLayer as updateLayerOp,
  removeLayer as removeLayerOp,
  moveLayer as moveLayerOp,
} from '@/lib/layers/operations';
import type { MediaLayer } from '@/lib/layers/types';

/** Per-clip media-layer state with debounced persistence to
 *  clip.data.editorLayers (backend shallow-merges, so other data keys survive). */
export function useMediaLayers(clipId: string | null, initialLayers: MediaLayer[]) {
  const [layers, setLayers] = useState<MediaLayer[]>(initialLayers);
  const [activeLayerId, setActiveLayerId] = useState<string | null>(null);
  const activeClipIdRef = useRef(clipId);
  const hasLocalChangesRef = useRef(false);
  const [updateClipData] = useMutation(UPDATE_CLIP_DATA);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The write the debounce timer will send, tagged with ITS clip. Tagging is
  // what lets an edit on the next clip flush — rather than cancel — a write
  // still pending for the previous one.
  const pendingWrite = useRef<{ clipId: string; layers: MediaLayer[] } | null>(null);

  const send = useCallback((write: { clipId: string; layers: MediaLayer[] }) => {
    updateClipData({ variables: { id: write.clipId, data: { editorLayers: write.layers } } })
      .catch((err) => console.error('Failed to persist layers', err));
  }, [updateClipData]);

  /** Fire any pending write immediately. Never drops it. */
  const flushPending = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const pending = pendingWrite.current;
    pendingWrite.current = null;
    if (pending) send(pending);
  }, [send]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(flushPending);
  const flushPendingRef = useRef(flushPending);
  useEffect(() => {
    flushPendingRef.current = flushPending;
  }, [flushPending]);

  const initialLayersSignature = JSON.stringify(initialLayers);

  // Reload when the selected clip changes. Also accept late async defaults
  // (notably the facecam suggestion) until the user has edited this clip.
  useEffect(() => {
    const clipChanged = activeClipIdRef.current !== clipId;
    if (clipChanged) {
      activeClipIdRef.current = clipId;
      hasLocalChangesRef.current = false;
      setActiveLayerId(null);
    }
    if (clipChanged || !hasLocalChangesRef.current) {
      setLayers(initialLayers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipId, initialLayersSignature]);

  // Unmount: a pending write must reach the backend, not die with the timer.
  useEffect(() => () => flushPendingRef.current(), []);

  const persist = useCallback((next: MediaLayer[]) => {
    if (!clipId) return;
    // A write still pending for ANOTHER clip is not superseded by this one —
    // fire it now. (Clearing the shared timer used to silently cancel it:
    // edit clip A, switch, edit clip B within the debounce → A's edit lost.)
    if (pendingWrite.current && pendingWrite.current.clipId !== clipId) {
      flushPending();
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    pendingWrite.current = { clipId, layers: next };
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      const pending = pendingWrite.current;
      pendingWrite.current = null;
      if (pending) send(pending);
    }, 600);
  }, [clipId, flushPending, send]);

  const commit = useCallback((next: MediaLayer[]) => {
    hasLocalChangesRef.current = true;
    setLayers(next);
    persist(next);
  }, [persist]);

  const addLayer = useCallback((layer: MediaLayer) => {
    hasLocalChangesRef.current = true;
    setLayers((prev) => {
      const next = addLayerOp(prev, layer);
      persist(next);
      return next;
    });
    setActiveLayerId(layer.id);
  }, [persist]);

  const patchLayer = useCallback((id: string, patch: Partial<MediaLayer>) => {
    hasLocalChangesRef.current = true;
    setLayers((prev) => {
      const next = updateLayerOp(prev, id, patch);
      persist(next);
      return next;
    });
  }, [persist]);

  /** Restack a layer (array order is z-order). A no-op move returns the same
   *  array from moveLayerOp, so we skip the state update AND the persist —
   *  otherwise every pointermove of a lane drag would queue a write. */
  const moveLayer = useCallback((id: string, toIndex: number) => {
    setLayers((prev) => {
      const next = moveLayerOp(prev, id, toIndex);
      if (next === prev) return prev;
      hasLocalChangesRef.current = true;
      persist(next);
      return next;
    });
  }, [persist]);

  const deleteLayer = useCallback((id: string) => {
    hasLocalChangesRef.current = true;
    setLayers((prev) => {
      const next = removeLayerOp(prev, id);
      persist(next);
      return next;
    });
    setActiveLayerId((cur) => (cur === id ? null : cur));
  }, [persist]);

  return { layers, activeLayerId, setActiveLayerId, addLayer, patchLayer, moveLayer, deleteLayer, commit };
}
